import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import { useLocation } from 'react-router-dom';
import { GeneralPopup, PageHeader, PageWrapper, PopupTypes } from '../../components/partials';
import Table from '../../components/organisms/Table';
import css from '../style.css';
import { getImpersonationFromQuery, parseSearchParams } from '../../utils/url';
import {
    buildTestReportRedirectUrl,
    DATA_SOURCES_STATUSES,
    ETL_FEATURE_FLAG,
    ETLS_STATES,
    isSingularTrackerUan,
    NETWORKS_WITH_ACCOUNTS_NAMES,
    PAGE_DATA_TYPES,
    parseMetadata,
    searchDataSources,
    shouldShowDeleteButton,
    shouldShowExport2Phase,
    shouldShowFieldsAvailability,
    shouldShowRunTestReport,
    STATUSES_COLOR_MAP,
    translateMessage,
    UAN_ACTION_MSG_BY_TYPE,
} from '../utils';
import { MESSAGE_ONCLICK_ACTION, SLACK_AUTH_URL, TOUR_LINK_IDS } from '../../utils/consts';
import TableCellIcon from '../../components/widgets/TableCellIcon';
import MonetizationIcon from '../../resources/svg/monetization.svg';
import Export2PhaseIcon from '../../resources/svg/raw-data.svg';
import TestReportIcon from '../../resources/svg/test_report.svg';
import EmailIcon from '../../resources/icons/dataConnectors/Email.svg';
import S3Icon from '../../resources/icons/dataConnectors/S3.svg';
import GDriveIcon from '../../resources/icons/dataConnectors/Gdrive.svg';
import APIIcon from '../../resources/icons/dataConnectors/api.svg';
import SFTPIcon from '../../resources/icons/dataConnectors/sftp.svg';
import EditIcon from '../../resources/svg/edit.svg';
import SlackIcon from '../../resources/svg/icons/slack-icon.svg';
import TrashIcon from '../../resources/svg/trash.svg';
import GlassesIcon from '../../resources/svg/glasses.svg';
import DataRefreshIcon from '../../resources/svg/data-refresh.svg';
import DataAvailabilityIcon from '../../resources/svg/data-availability.svg';
import DataConnectorAlertsIcon from '../../resources/svg/data_connector_alerts.svg';
import { sortBool, sortByType } from '../../utils/sortUtil';
import { getAdnetworkDisplayName } from '../../utils/adNetworks';
import { Button, EmptyState, Shelf, Spinner, Toggle } from '../../components/widgets';
import AdNetworksEmptyState from '../../components/widgets/AdNetworksEmptyState';
import WizardWarningMessage from '../../teamManagement/components/WizardWarningMessage';
import AdnetworkBasicLogin from '../containers/AdnetworkBasicLogin';
import DataConnectorAlerts from './DataConnectorAlerts';
import YfDataStatus from './yfDataStatus/YfDataStatus';
import Logger from '../../services/logger';
import ProgressCircle1 from '../../resources/svg/etl_states/progress-circle-1-of-4.svg';
import ProgressCircle2 from '../../resources/svg/etl_states/progress-circle-2-of-4.svg';
import ProgressCircle3 from '../../resources/svg/etl_states/progress-circle-3-of-4.svg';
import ProgressCircle4 from '../../resources/svg/etl_states/progress-circle-4-of-4.svg';
import ETLStatusUpToDate from '../../resources/svg/etl_states/etl_state_green.svg';
import ETLStatusInProgress from '../../resources/svg/etl_states/etl_state_blue.svg';
import ETLStatusDisabled from '../../resources/svg/etl_states/etl_state_disabled.svg';
import ETLStatusNoETL from '../../resources/svg/etl_states/etl_state_no_etl.svg';
import ETLStatusNotAllActive from '../../resources/svg/etl_states/etl_state_red.svg';
import Tooltip from '../../components/molecules/Tooltip';
import DataSourcesService from '../service';
import { downloadFileResponse } from '../../global/utils';
import toaster from '../../utils/toaster';

import TextField from '../../components/molecules/TextField';
import AddNewUan from './AddNewUan';
import { route } from '../../utils/routerUtils';
import { DATA_CONNECTORS_EVENT_PREFIX, trackMixpanelEvent } from '../../utils/general';
import SchemaColumnsShelf from './SchemaColumnsShelf';
import EyeIcon from '../../resources/svg/show_columns.svg';
import TableCellDestination from '../../components/widgets/TableCellDestination';
import TableCellImgLabelWithAgency from '../../components/widgets/TableCellImgLabelWithAgency';
import TableCellPopover from '../../components/widgets/TableCellPopOver';
import EtlPopover from './EtlPopover';
import DataShelf from './DataShelf';
import TableCellActions from '../../components/widgets/TableCellActions';
import UpgradeETLBanner from './UpgradeETLBanner';
import FieldsAvailability from '../../fieldsAvailability/components/fieldsAvailability';
import { reportTypes } from '../../utils/reports';

const logger = new Logger('[dataSourcesPage]');
const DataSourcesAPI = new DataSourcesService();

let hoverStartTime = null;
let dataRefreshHoverStart = null;
let dataAvailabilityHoverStart = null;

const loadingText = (text, step) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span key="text">{text}</span>
        <span key="sub-text" style={{ color: '#64748B', fontSize: 12 }}>
            {`Step ${step} of 4`}
        </span>
    </div>
);

const DEFAULT_UANS_DATA = [];
const DEFAULT_ADNETWORKS = {};
const DEFAULT_PARTNERS_IDS = new Set();
const DEFAULT_SELECTED_UAN_DATA = {};
const DEFAULT_SHELF = { open: false };
const DEFAULT_UAN_ACTION = {};
const DEFAULT_AD_NETWORK_OPTIONS = [];
const DEFAULT_EMPTY_STATE_DATA_CONNECTORS = ['adwords', 'facebook', 'twitter', 'snapchat', 'applesearchads', 'tiktok'];
const DEFAULT_FEATURE_FLAGS = [];
const DEFAULT_MIXPANEL_USER_PARAMS = {};

const DataSourcesPage = ({
    uansData = DEFAULT_UANS_DATA,
    adnetworks = DEFAULT_ADNETWORKS,
    partnersIds = DEFAULT_PARTNERS_IDS,
    adminMode = false,
    translate = () => {},
    export2Phase = () => {},
    openShelf = () => {},
    onCloseShelf = () => {},
    onUanDelete = () => {},
    onDeleteUserSlackAuth = () => {},
    selectedUanData = DEFAULT_SELECTED_UAN_DATA,
    shelf = DEFAULT_SHELF,
    apiRequestFailed = false,
    export2phaseLatestTaskKey = '',
    username = '',
    mixpanelUserParams = DEFAULT_MIXPANEL_USER_PARAMS,
    isLoadingUans,
    uanAction = DEFAULT_UAN_ACTION,
    updatedUanId = null,
    timezone = '',
    onValidationFinished,
    adNetworkOptions = DEFAULT_AD_NETWORK_OPTIONS,
    redirectPath = 'react/data-sources',
    translationPageKey = PAGE_DATA_TYPES.DATA_SOURCES,
    archivedToggleEnabled = true,
    emptyStateDataConnectors = DEFAULT_EMPTY_STATE_DATA_CONNECTORS,
    mixpanelPrefix = DATA_CONNECTORS_EVENT_PREFIX,
    template2phaseLink = '/react/logs/download?user={user_name}&api_type=download_2phase&task_id={task_id}',
    isUserSlackAuth = false,
    isStandardAnalytics = false,
    showFieldsAvailabilityFeature = false,
    featureFlags = DEFAULT_FEATURE_FLAGS,
    etlStatusEnabled = false,
    externalShelfElement,
}) => {
    const location = useLocation();

    const [searchText, setSearchText] = useState('');
    const [showArchivedUans, setShowArchivedUans] = useState(!archivedToggleEnabled);
    const [sortedColName, setSortedColName] = useState('status');
    const [sortType, setSortType] = useState('number');
    const [isSortAscending, setIsSortAscending] = useState(true);
    const [selectedUan, setSelectedUan] = useState(null);
    const [selectedUanAdnetworkName, setSelectedUanAdnetworkName] = useState(null);
    const [dataShelfUan, setDataShelfUan] = useState(null);
    const [dataConnectorAlertsUan, setDataConnectorAlertsUan] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [uanDeleteInProcess, setUanDeleteInProcess] = useState(false);
    const [lastUpdatedUanId, setLastUpdatedUanId] = useState(null);
    const [openFieldsAvailability, setOpenFieldsAvailability] = useState(false);
    const [openYfDataStatus, setOpenYfDataStatus] = useState(false);
    const [openDataRefresh, setOpenDataRefresh] = useState(false);
    const [openDataConnectorAlerts, setOpenDataConnectorAlerts] = useState(false);
    const [schemaColumnsUan, setSchemaColumnsUan] = useState(null);
    const [adnetworkBasicLoginHasChanges, setAdnetworkBasicLoginHasChanges] = useState(false);
    const [uanIdToShowFirst, setUanIdToShowFirst] = useState(undefined);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [uanIdToDelete, setUanIdToDelete] = useState(null);
    const [uanToDeleteWarnings, setUanToDeleteWarnings] = useState([]);

    const pageRef = useRef(null);
    const prevShelfOpenRef = useRef(shelf.open);
    const prevUpdatedUanIdRef = useRef(updatedUanId);
    const prevUanActionRef = useRef(uanAction);
    const prevExport2phaseLatestTaskKeyRef = useRef(export2phaseLatestTaskKey);

    useEffect(() => {
        trackMixpanelEvent(mixpanelPrefix, 'Page View', {});

        const handleValidationFinished = ({ detail }) => {
            onValidationFinished(detail.uanId);
        };

        window.addEventListener('validationFinished', handleValidationFinished);

        return () => {
            window.removeEventListener('validationFinished', handleValidationFinished);
        };
    }, []);

    useEffect(() => {
        const hasKeyChanged = prevExport2phaseLatestTaskKeyRef.current !== export2phaseLatestTaskKey;
        if (export2phaseLatestTaskKey && hasKeyChanged) {
            let link = template2phaseLink
                .replace('{user_name}', username)
                .replace('{task_id}', export2phaseLatestTaskKey);
            const impersonatedUser = getImpersonationFromQuery();
            if (impersonatedUser) {
                link += `&user=${decodeURIComponent(impersonatedUser)}`;
            }
            window.open(link, '_blank');
        }
        prevExport2phaseLatestTaskKeyRef.current = export2phaseLatestTaskKey;
    }, [export2phaseLatestTaskKey]);

    useEffect(() => {
        setSelectedUan(selectedUanData);
    }, [selectedUanData]);

    useEffect(() => {
        if (uanAction.uan && uanAction.type && uanAction.type !== 'delete') {
            setUanIdToShowFirst(uanAction.uan.uan_id);
        } else if (!uanAction.type) {
            setLastUpdatedUanId(uanIdToShowFirst);
            setUanIdToShowFirst(undefined);
        }
    }, [uanAction, uanIdToShowFirst]);

    useEffect(() => {
        if (updatedUanId === null && prevUpdatedUanIdRef.current !== null) {
            setLastUpdatedUanId(uanIdToShowFirst);
            setUanIdToShowFirst(updatedUanId);
        } else {
            setUanIdToShowFirst(updatedUanId);
        }
        prevUpdatedUanIdRef.current = updatedUanId;
    }, [updatedUanId]);

    useEffect(() => {
        if (apiRequestFailed && !showErrorPopup) {
            setShowErrorPopup(true);
        }
    }, [apiRequestFailed, showErrorPopup]);

    const onEditUanClick = useCallback(
        (rowData, isMailEdit = false) => {
            trackMixpanelEvent(mixpanelPrefix, 'Clicked Edit Data Connector', {
                ...rowData,
                has_validation_error: Boolean(
                    rowData.report_validation_error && Object.keys(rowData.report_validation_error).length > 0
                ),
                isMailEdit,
            });

            setSelectedRowData(rowData);
            setSelectedUan({ uan_id: rowData.uanId, dataConnectorId: rowData.dataConnectorId });
            openShelf(rowData.displayName, rowData.uanId);
            route(redirectPath, { id: rowData.uanId });
        },
        [openShelf, redirectPath, mixpanelPrefix]
    );

    const addNewUan = useCallback(
        selectedUanValue => {
            setSelectedUan(selectedUanValue);
            openShelf(selectedUanValue.display_name);
        },
        [openShelf]
    );

    useEffect(() => {
        if (!shelf.open) {
            setSelectedRowData(null);
            setAdnetworkBasicLoginHasChanges(false);

            if (location && !isLoadingUans && !(selectedUan?.uan_id || selectedUan?.id)) {
                const urlParams = parseSearchParams(location.search);
                const id = urlParams.id ? parseInt(urlParams.id, 10) : null;

                const editedUan = uansData.find(uan => uan.uanId === id);
                const selectedUanFromAdnetworks = Object.values(adnetworks).find(adn => adn.id === id);

                if (editedUan) {
                    onEditUanClick(editedUan, !!urlParams['is-mail']);
                } else if (selectedUanFromAdnetworks) {
                    addNewUan(selectedUanFromAdnetworks);
                } else if (id) {
                    route(redirectPath);
                }
            }
        }
    }, [
        shelf.open,
        uansData,
        adnetworks,
        location,
        redirectPath,
        isLoadingUans,
        onEditUanClick,
        addNewUan,
        selectedUan,
    ]);

    useEffect(() => {
        if (prevShelfOpenRef.current && !shelf.open) {
            route(redirectPath);
        }

        prevShelfOpenRef.current = shelf.open;
    }, [shelf.open]);

    useEffect(() => {
        if (uanAction.type && prevUanActionRef.current !== uanAction) {
            document.querySelector('#reactContainer').scrollTo(0, 0);
        }

        prevUanActionRef.current = uanAction;
    }, [uanAction]);

    const defaultThrottle = useCallback(func => {
        return throttle(func, 3000, { trailing: false });
    }, []);

    const onSearchChange = useCallback(
        searchTextValue => {
            trackMixpanelEvent(mixpanelPrefix, 'Filter Data Connector', { text: searchTextValue });
            setSearchText(searchTextValue);
        },
        [mixpanelPrefix]
    );

    const onExport2PhaseClick = useCallback(
        rowData => {
            trackMixpanelEvent(mixpanelPrefix, 'Clicked Download Raw CSV');

            export2Phase(rowData.uanId);
        },
        [export2Phase, mixpanelPrefix]
    );

    const onDownloadPermissionErrorsClick = useCallback(
        async rowData => {
            try {
                const resp = await DataSourcesAPI.downloadPermissionErrors(rowData.uanId);

                if (!resp || resp.contents == null) {
                    toaster.info(translate('STATIC.PAGES.DATA_SOURCES.NO_PERMISSION_ERRORS_MESSAGE'));
                    return;
                }

                const { contents } = resp;

                if (typeof contents === 'object') {
                    toaster.info(translate('STATIC.PAGES.DATA_SOURCES.NO_PERMISSION_ERRORS_MESSAGE'));
                    return;
                }

                downloadFileResponse(resp, false);
            } catch (e) {
                toaster.error(translate('STATIC.GENERAL_MESSAGES.UNEXPECTED_ERROR'));
            }
        },
        [translate]
    );

    const onRunTestReportClick = useCallback(
        rowData => {
            trackMixpanelEvent(mixpanelPrefix, 'Clicked Run Test Report');

            window.open(buildTestReportRedirectUrl(rowData.uanId, isStandardAnalytics), '_blank');
        },
        [isStandardAnalytics, mixpanelPrefix]
    );

    const shouldDisplayEtlStatus = useCallback(() => {
        return (
            redirectPath === 'react/data-sources' &&
            featureFlags.length &&
            featureFlags.includes('singular_etl') &&
            etlStatusEnabled
        );
    }, [featureFlags, redirectPath, etlStatusEnabled]);

    const toggleFieldsAvailabilityShelf = useCallback((rowData, isOpen) => {
        setOpenFieldsAvailability(isOpen);
        setSelectedUanAdnetworkName(rowData.adnetworkName);
    }, []);

    const toggleDataRefreshShelf = useCallback((rowData, openDataRefreshValue, openDataShelf = true) => {
        if (!openDataShelf) {
            setOpenDataRefresh(false);
            setDataShelfUan(null);
            return;
        }

        setSelectedRowData(rowData);
        setOpenDataRefresh(openDataRefreshValue);
        setDataShelfUan({
            canDataRefresh: rowData.canDataRefresh,
            earliestDataDate: rowData.earliestDataDate,
            orgTimezone: rowData.orgTimezone,
            uanId: rowData.uanId,
            displayName: rowData.displayName,
            status: rowData.status,
            imgSrc: rowData.imgSrc,
            channel: rowData.channel,
        });
    }, []);

    const toggleDataConnectorAlertsShelf = useCallback((rowData, isOpen) => {
        setOpenDataConnectorAlerts(isOpen);
        setDataConnectorAlertsUan({
            uanId: rowData.uanId,
            displayName: rowData.displayName,
        });
    }, []);

    const toggleSchemaColumnsShelf = useCallback(
        rowData => {
            if (rowData) {
                trackMixpanelEvent(mixpanelPrefix, 'Data Destinations - show schema columns', { uanId: rowData.uanId });
            }
            setSchemaColumnsUan(rowData ? { uanId: rowData.uanId } : null);
        },
        [mixpanelPrefix]
    );

    const openYfDataStatusShelf = useCallback(() => {
        trackMixpanelEvent(mixpanelPrefix, 'Opened Data Delta Status');
        setOpenYfDataStatus(true);
    }, [mixpanelPrefix]);

    const closeFieldsAvailabilityShelf = useCallback(() => {
        toggleFieldsAvailabilityShelf({}, false);
    }, [toggleFieldsAvailabilityShelf]);

    const onSlackAction = useCallback(() => {
        trackMixpanelEvent(mixpanelPrefix, 'Slack Auth', {
            isEnabled: !isUserSlackAuth,
        });

        if (isUserSlackAuth) {
            onDeleteUserSlackAuth();
        } else {
            window.location.href = SLACK_AUTH_URL;
        }
    }, [isUserSlackAuth, onDeleteUserSlackAuth, mixpanelPrefix]);

    const onDeleteUanClick = useCallback(rowData => {
        setUanIdToDelete(rowData.uanId);
        setUanToDeleteWarnings(rowData.customConfigWarnings || []);
        setShowDeleteDialog(true);
    }, []);

    const onPopupClose = useCallback(() => {
        setUanIdToDelete(null);
        setUanToDeleteWarnings([]);
        setShowDeleteDialog(false);
        setShowErrorPopup(false);
        setShowCancelDialog(false);
    }, []);

    const onAcceptUanDelete = useCallback(() => {
        setUanDeleteInProcess(true);
        onUanDelete(uanIdToDelete);
        setTimeout(() => {
            setShowDeleteDialog(false);
            setUanDeleteInProcess(false);
        }, 3000);
    }, [onUanDelete, uanIdToDelete]);

    const onAcceptCancelAdnetworkLoginShelf = useCallback(() => {
        onCloseShelf();
        setShowCancelDialog(false);
        setAdnetworkBasicLoginHasChanges(false);
    }, [onCloseShelf]);

    const onAdnetworkLoginChange = useCallback(hasChanges => {
        setAdnetworkBasicLoginHasChanges(hasChanges);
    }, []);

    const onAdNetworkSelectionChange = useCallback(
        selectedAdNetworkOption => {
            if (!selectedAdNetworkOption) {
                logger.log('No network selected!');
            } else {
                trackMixpanelEvent(mixpanelPrefix, 'Add UAN', { name: selectedAdNetworkOption.value }, true);

                if (Object.keys(adnetworks).length) {
                    const adnetworkName = selectedAdNetworkOption.value
                        ? selectedAdNetworkOption.value
                        : Object.keys(adnetworks).find(key => adnetworks[key].id === selectedAdNetworkOption.id);

                    const selectedUanValue = adnetworks[adnetworkName];
                    if (!selectedUanValue) {
                        return;
                    }

                    addNewUan(selectedUanValue);
                    route(redirectPath, { id: selectedUanValue.id });
                }
            }
        },
        [adnetworks, redirectPath, mixpanelPrefix, addNewUan]
    );

    const changeSort = useCallback(
        (colName, type) => {
            const nextIsSortAscending = sortedColName === colName ? !isSortAscending : false;

            trackMixpanelEvent(mixpanelPrefix, 'Clicked Sort', {
                column_name: colName,
                is_ascending: nextIsSortAscending,
            });
            const mixpanelParams = { name: colName, type: nextIsSortAscending ? 'asc' : 'desc' };
            trackMixpanelEvent(mixpanelPrefix, 'Table Sort', mixpanelParams, true);

            setSortedColName(colName);
            setSortType(type);
            setIsSortAscending(nextIsSortAscending);
            setUanIdToShowFirst(undefined);
        },
        [sortedColName, isSortAscending, mixpanelPrefix]
    );

    const onToggleArchivedUans = useCallback(
        (event, checked) => {
            trackMixpanelEvent(mixpanelPrefix, 'Clicked 30 Day Toggle', { turned: checked ? 'on' : 'off' });
            setShowArchivedUans(checked);
        },
        [mixpanelPrefix]
    );

    const onMessageTextClick = useCallback(
        (action, rowData) => {
            if (action === MESSAGE_ONCLICK_ACTION.OPEN_SHELF && rowData) {
                onEditUanClick(rowData);
            }
        },
        [onEditUanClick]
    );

    const getActionComponentsConfigs = useCallback(() => {
        return [
            {
                ActionComponent: DataAvailabilityIcon,
                onClick: defaultThrottle(rowData => toggleDataRefreshShelf(rowData, false)),
                key: 'dataAvailability',
                title: translate('STATIC.PAGES.DATA_SOURCES.DATA_AVAILABILITY_TITLE'),
                shouldShow: uanData => uanData.canDataAvailability,
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.DATA_AVAILABILITY_TOOLTIP'),
                onMouseEnter: () => {
                    dataAvailabilityHoverStart = new Date();
                },
                onMouseLeave: () => {
                    const hoverTime = new Date() - dataAvailabilityHoverStart;
                    if (hoverTime > 1000) {
                        trackMixpanelEvent(mixpanelPrefix, 'Hover on Data Availability icon', {
                            hoverTime,
                        });
                    }
                },
            },
            {
                ActionComponent: DataRefreshIcon,
                onClick: defaultThrottle(rowData => toggleDataRefreshShelf(rowData, true)),
                onMouseEnter: () => {
                    dataRefreshHoverStart = new Date();
                },
                onMouseLeave: () => {
                    const hoverTime = new Date() - dataRefreshHoverStart;
                    if (hoverTime > 1000) {
                        trackMixpanelEvent(mixpanelPrefix, 'Hover on Data Refresh icon', {
                            hoverTime,
                        });
                    }
                },
                key: 'dataRefresh',
                title: translate('STATIC.PAGES.DATA_SOURCES.DATA_REFRESH_TITLE'),
                shouldShow: uanData => uanData.canDataRefresh,
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.DATA_REFRESH_TOOLTIP'),
            },
            {
                ActionComponent: GlassesIcon,
                onClick: defaultThrottle(rowData => toggleFieldsAvailabilityShelf(rowData, true)),
                key: 'fieldsAvailability',
                title: translate('STATIC.PAGES.DATA_SOURCES.FIELDS_AVAILABILITY_TITLE'),
                shouldShow: uanData => {
                    return shouldShowFieldsAvailability(uanData, showFieldsAvailabilityFeature);
                },
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.OPEN_FIELDS_AVAILABILITY_TOOLTIP'),
            },
            {
                ActionComponent: EyeIcon,
                onClick: defaultThrottle(toggleSchemaColumnsShelf),
                key: 'schemaColumns',
                title: translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.TITLE'),
                shouldShow: () => translationPageKey === PAGE_DATA_TYPES.DATA_DESTINATIONS,
                tooltipText: translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.TOOLTIP'),
            },
            {
                ActionComponent: EditIcon,
                onClick: defaultThrottle(onEditUanClick),
                key: 'editUan',
                title: translate('STATIC.PAGES.DATA_SOURCES.EDIT_UAN_TITLE'),
                shouldShow: uanData => !isSingularTrackerUan(uanData.displayName),
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.EDIT_UAN_TOOLTIP'),
            },
            {
                ActionComponent: DataConnectorAlertsIcon,
                onClick: defaultThrottle(rowData => toggleDataConnectorAlertsShelf(rowData, true)),
                key: 'dataConnectorAlerts',
                badge: translate('STATIC.PAGES.DATA_SOURCES.NEW_BADGE'),
                title: translate('STATIC.PAGES.DATA_SOURCES.DATA_CONNECTOR_ALERTS_TITLE'),
                shouldShow: uanData => uanData.canDataConnectorAlerts,
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.DATA_CONNECTOR_ALERTS_TOOLTIP'),
            },
            {
                ActionComponent: Export2PhaseIcon,
                onClick: defaultThrottle(onExport2PhaseClick),
                key: 'export2Phase',
                title: translate('STATIC.PAGES.DATA_SOURCES.EXPORT_RAW_DATA_TITLE'),
                shouldShow: shouldShowExport2Phase,
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.EXPORT_RAW_DATA'),
            },
            {
                ActionComponent: TestReportIcon,
                onClick: defaultThrottle(onRunTestReportClick),
                key: 'runTestReport',
                title: translate('STATIC.PAGES.DATA_SOURCES.RUN_TEST_REPORT_TITLE'),
                shouldShow: shouldShowRunTestReport,
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.RUN_TEST_REPORT_TOOLTIP'),
            },
            {
                ActionComponent: Export2PhaseIcon,
                onClick: defaultThrottle(onDownloadPermissionErrorsClick),
                key: 'downloadPermissionErrors',
                title: translate('STATIC.PAGES.DATA_SOURCES.DOWNLOAD_PERMISSION_ERRORS_TITLE'),
                shouldShow: () => translationPageKey === PAGE_DATA_TYPES.DATA_SOURCES,
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.DOWNLOAD_PERMISSION_ERRORS_TOOLTIP'),
            },
            {
                ActionComponent: TrashIcon,
                onClick: defaultThrottle(onDeleteUanClick),
                key: 'deleteUan',
                shouldShow: shouldShowDeleteButton,
                tooltipText: translate('STATIC.PAGES.DATA_SOURCES.DELETE_UAN_TOOLTIP'),
                title: translate('STATIC.PAGES.DATA_SOURCES.DELETE_UAN_TITLE'),
            },
        ];
    }, [
        translate,
        showFieldsAvailabilityFeature,
        defaultThrottle,
        toggleDataRefreshShelf,
        toggleFieldsAvailabilityShelf,
        toggleSchemaColumnsShelf,
        onEditUanClick,
        toggleDataConnectorAlertsShelf,
        onExport2PhaseClick,
        onRunTestReportClick,
        onDeleteUanClick,
        mixpanelPrefix,
        translationPageKey,
    ]);

    const getMetadataConfig = useCallback(
        (actionComponentsConfigs, isETL) => {
            const channelImages = { mail: EmailIcon, s3: S3Icon, gdrive: GDriveIcon, api: APIIcon, ftp: SFTPIcon };
            const channelProperName = {
                mail: 'a mail',
                s3: 'an S3',
                gdrive: 'a GDrive',
                api: 'an API',
                ftp: 'an SFTP',
            };

            const etlStatusConfig = {
                name: 'etlStatus',
                displayName: translate(`STATIC.PAGES.${translationPageKey}.TABLE.HEADERS.ETL_STATUS`),
                headerAlign: 'center',
                cellComponent: TableCellPopover,
                thStyle: { minWidth: '98.5px' },
                childrenComponent: EtlPopover,
                childrenComponentProps: [
                    { propName: 'header', dataKey: 'etlPopoverHeader' },
                    { propName: 'etlTableContent', dataKey: 'etlPopoverContent' },
                ],
                cellProps: {
                    tdStyle: {
                        minWidth: '98.5px',
                        height: 50,
                        paddingRight: '0px',
                    },
                    iconElement: {
                        [ETLS_STATES.UP_TO_DATE]: <ETLStatusUpToDate />,
                        [ETLS_STATES.SOME_IN_PROGRESS]: <ETLStatusInProgress />,
                        [ETLS_STATES.ALL_IN_PROGRESS]: <ETLStatusInProgress />,
                        [ETLS_STATES.NOT_EXIST]: <ETLStatusNoETL />,
                        [ETLS_STATES.NOT_ALL_ACTIVE]: <ETLStatusNotAllActive />,
                        [ETLS_STATES.NO_STATE]: <ETLStatusDisabled />,
                        [ETLS_STATES.NOT_SUPPORTED]: <ETLStatusNoETL />,
                    },
                    tooltip: {
                        [ETLS_STATES.UP_TO_DATE]: `STATIC.PAGES.${translationPageKey}.TABLE.CELLS.ETL_STATUS.TOOLTIPS.UP_TO_DATE`,
                        [ETLS_STATES.SOME_IN_PROGRESS]: `STATIC.PAGES.${translationPageKey}.TABLE.CELLS.ETL_STATUS.TOOLTIPS.SOME_IN_PROGRESS`,
                        [ETLS_STATES.ALL_IN_PROGRESS]: `STATIC.PAGES.${translationPageKey}.TABLE.CELLS.ETL_STATUS.TOOLTIPS.ALL_IN_PROGRESS`,
                        [ETLS_STATES.NOT_EXIST]: `STATIC.PAGES.${translationPageKey}.TABLE.CELLS.ETL_STATUS.TOOLTIPS.NOT_EXIST`,
                        [ETLS_STATES.NOT_ALL_ACTIVE]: `STATIC.PAGES.${translationPageKey}.TABLE.CELLS.ETL_STATUS.TOOLTIPS.NOT_ALL_ACTIVE`,
                        [ETLS_STATES.NOT_SUPPORTED]: `STATIC.PAGES.${translationPageKey}.TABLE.CELLS.ETL_STATUS.TOOLTIPS.NOT_SUPPORTED`,
                    },
                    hasPopover: {
                        [ETLS_STATES.UP_TO_DATE]: true,
                        [ETLS_STATES.SOME_IN_PROGRESS]: true,
                        [ETLS_STATES.ALL_IN_PROGRESS]: true,
                        [ETLS_STATES.NOT_EXIST]: false,
                        [ETLS_STATES.NOT_ALL_ACTIVE]: true,
                        [ETLS_STATES.NO_STATE]: false,
                        [ETLS_STATES.NOT_SUPPORTED]: false,
                    },
                    translate,
                },
                cellValues: [{ propName: 'isAdMonetization', dataKey: 'is_ad_monetization' }],
            };

            const metadataConfig = [
                {
                    name: 'displayName',
                    displayName: translate(`STATIC.PAGES.${translationPageKey}.TABLE.HEADERS.DISPLAY_NAME`),
                    headerAlign: 'center',
                    cellComponent: TableCellImgLabelWithAgency,
                    showHeaderArrow: true,
                    cellValues: [
                        { propName: 'valueSuffix', dataKey: 'displayNameSuffix' },
                        { propName: 'subText', dataKey: 'subText' },
                        { propName: 'subTextExtra', dataKey: 'subTextExtra' },
                        { propName: 'agencyName', dataKey: 'agencyName' },
                        { propName: 'imgSrc', dataKey: 'imgSrc' },
                        { propName: 'markCell', dataKey: 'showInArchive' },
                        { propName: 'showImgBadge', dataKey: 'is_ad_monetization' },
                        { propName: 'showUsernameImg', dataKey: 'showUsernameImg' },
                        { propName: 'SideImg', dataKey: 'channel', transformation: channel => channelImages[channel] },
                        {
                            propName: 'sideImgHoverText',
                            dataKey: 'channel',
                            transformation: channel => `This connector is ${channelProperName[channel]} integration`,
                        },
                    ],
                    headerClick: () => changeSort('displayName', 'string'),
                    thStyle: { minWidth: '400px' },
                    cellProps: {
                        tdStyle: {
                            minWidth: '400px',
                            maxWidth: '400px',
                        },
                        containerStyle: {
                            padding: '5px',
                        },
                        imgStyle: {
                            height: '30px',
                            maxWidth: '30px',
                            margin: '0 20px 0 0',
                            borderRadius: '3px',
                        },
                        labelsStyle: {
                            maxWidth: '83%',
                        },
                        valueClassName: css.dataConnectorName,
                        valueSuffixClassName: css.dataConnectorId,
                        useOverflowingTextForValue: true,
                        subTextStyle: {
                            fontSize: '12px',
                            color: 'black',
                            maxWidth: '180px',
                        },
                        subTextExtraStyle: {
                            fontSize: '12px',
                            color: '#334155',
                            maxWidth: '180px',
                        },
                        markCellTooltipText: 'Connector without data in the last 30 days',
                        Icon: MonetizationIcon,
                    },
                },
                {
                    name: 'status',
                    subValue: 'message',
                    isBold: true,
                    displayName: 'Status',
                    headerAlign: 'center',
                    cellComponent: TableCellIcon,
                    thStyle: { width: '80%' },
                    headerClick: () => changeSort('status', 'number'),
                    cellValues: [{ propName: 'isLoading', dataKey: 'isUanLoading' }],
                    cellProps: {
                        color: STATUSES_COLOR_MAP,
                        tdStyle: { width: '80%', height: 50 },
                        iconElement: {
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_SCRAPING]: <ProgressCircle1 />,
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_DATA_SOURCE_PIPELINE]: <ProgressCircle2 />,
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_SOURCE_PIPELINE]: <ProgressCircle3 />,
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_DRUID_LOAD]: <ProgressCircle4 />,
                        },
                        text: {
                            [DATA_SOURCES_STATUSES.ACTIVE]: 'Active',
                            [DATA_SOURCES_STATUSES.PENDING]: 'Pending',
                            [DATA_SOURCES_STATUSES.INACTIVE]: 'Inactive',
                            [DATA_SOURCES_STATUSES.NOT_IMPLEMENTED]: 'Not Implemented',
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_SCRAPING]: loadingText('Pulling Data', 1),
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_DATA_SOURCE_PIPELINE]: loadingText(
                                'Normalizing Data',
                                2
                            ),
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_SOURCE_PIPELINE]: loadingText(
                                'Combining Data',
                                3
                            ),
                            [DATA_SOURCES_STATUSES.FIRST_SCRAPE_STAGE_DRUID_LOAD]: loadingText('Loading Data', 4),
                            [DATA_SOURCES_STATUSES.ACTION_REQUIRED]: 'Action Required',
                            [DATA_SOURCES_STATUSES.DATA_DELAY]: 'Delayed',
                            [DATA_SOURCES_STATUSES.PARTIAL_DATA]: 'Partial\u00a0Data',
                        },
                        onSubValueClick: onMessageTextClick,
                    },
                    showHeaderArrow: true,
                },
                {
                    name: 'lastDataImportDate',
                    displayName: translate(`STATIC.PAGES.${translationPageKey}.TABLE.HEADERS.LAST_DATA_IMPORT_DATE`),
                    thStyle: { minWidth: '180px' },
                    headerClick: () => changeSort('lastDataImportDate', 'date'),
                    cellValues: [{ propName: 'isLoading', dataKey: 'isUanLoading' }],
                    cellProps: {
                        containerStyle: {
                            minWidth: '180px',
                            textAlign: 'center',
                        },
                    },
                    showHeaderArrow: true,
                },
                {
                    name: 'actionItems',
                    cellComponent: TableCellActions,
                    displayName: '',
                    thStyle: { minWidth: '200px' },
                    cellProps: {
                        containerStyle: {
                            minWidth: '200px',
                            textAlign: 'right',
                        },
                        isETL,
                        actionComponentsConfigs,
                        extendedMenuTooltipText: translate(`STATIC.PAGES.DATA_SOURCES.MORE_ACTIONS_TOOLTIP`),
                    },
                },
            ];

            if (isETL) {
                metadataConfig[0] = {
                    name: 'displayName',
                    displayName: translate(`STATIC.PAGES.${translationPageKey}.TABLE.HEADERS.DISPLAY_NAME`),
                    headerAlign: 'center',
                    cellComponent: TableCellDestination,
                    showHeaderArrow: true,
                    cellValues: [
                        { propName: 'schemaName', dataKey: 'subTextExtra' },
                        { propName: 'filterValue', dataKey: 'subText' },
                        { propName: 'uanId', dataKey: 'uanId' },
                        { propName: 'agencyName', dataKey: 'agencyName' },
                        { propName: 'imgSrc', dataKey: 'imgSrc' },
                    ],
                    headerClick: () => changeSort('displayName', 'string'),
                    thStyle: { minWidth: '400px' },
                    cellProps: {
                        tdStyle: { minWidth: '400px', maxWidth: '400px' },
                        containerStyle: { padding: '5px' },
                        idLabel: translate('STATIC.PAGES.DATA_DESTINATIONS.TABLE.CELLS.ID_LABEL'),
                        filterLabel: translate('STATIC.PAGES.DATA_DESTINATIONS.TABLE.CELLS.FILTER_LABEL'),
                    },
                };
            }

            if (shouldDisplayEtlStatus()) {
                metadataConfig.splice(2, 0, etlStatusConfig);
            }

            return metadataConfig;
        },
        [translate, translationPageKey, changeSort, shouldDisplayEtlStatus, onMessageTextClick]
    );

    const handleRequestIntegration = useCallback(
        ({ name, website, additionalDetails }) => {
            trackMixpanelEvent(mixpanelPrefix, 'Requested Integration', {
                requested_source: name,
                website,
                notes: additionalDetails,
                event_source: 'frontend',
                ...mixpanelUserParams,
            });
            toaster.success(
                // Dark text for contrast on the lighter Green200 background (see style.css).
                <div style={{ color: 'var(--gray800)' }}>
                    <div style={{ fontWeight: 600 }}>
                        {translate(`STATIC.PAGES.${translationPageKey}.REQUEST_INTEGRATION_SUBMITTED_TITLE`)}
                    </div>
                    <div>
                        {translate(`STATIC.PAGES.${translationPageKey}.REQUEST_INTEGRATION_SUBMITTED_BODY`, {
                            name,
                        })}
                    </div>
                </div>,
                // The class brands this toast with the design-system Green200 instead of
                // react-toastify's default success green (see style.css). The toast renders
                // in a portal, so it's hooked via a plain global class rather than a CSS module.
                { autoClose: 5000, className: 'dataConnectorRequestToast' }
            );
        },
        [mixpanelPrefix, translate, translationPageKey, mixpanelUserParams]
    );

    const getNewUanComponent = useCallback(() => {
        const allowedAdns = adNetworkOptions.filter(adn => !adn.isDisabled);
        if (allowedAdns?.length === 1) {
            return (
                <Button
                    onClick={() => onAdNetworkSelectionChange(allowedAdns[0])}
                    type="primary"
                    level="level1"
                    text={`STATIC.PAGES.${translationPageKey}.ADD_NEW_SPECIFIC_UAN`}
                    textReplace={{ adnetwork: allowedAdns[0].label }}
                    className={css.addUanButton}
                />
            );
        }
        return (
            <AddNewUan
                adNetworkOptions={adNetworkOptions}
                onAdNetworkSelectionChange={onAdNetworkSelectionChange}
                buttonText={`STATIC.PAGES.${translationPageKey}.ADD_NEW_UAN`}
                placeholderText={`STATIC.PAGES.${translationPageKey}.ADNETWORK_SELECTION_PLACEHOLDER`}
                icon={redirectPath === 'react/data-sources' ? 'plus' : null}
                onInputChange={text =>
                    trackMixpanelEvent(mixpanelPrefix, 'Table State New UAN Search', { name: text }, true)
                }
                enableIntegrationRequest={redirectPath === 'react/data-sources'}
                onIntegrationRequested={handleRequestIntegration}
                translationPageKey={translationPageKey}
            />
        );
    }, [
        adNetworkOptions,
        redirectPath,
        translationPageKey,
        onAdNetworkSelectionChange,
        mixpanelPrefix,
        handleRequestIntegration,
    ]);

    const rawUansData = uansData || [];
    const { uan: uanFromUanAction = {}, type: uanActionType } = uanAction || {};
    const isETL = redirectPath === 'react/data-destinations' || redirectPath === 'react/extract-data-destinations';
    const hasEtlFlag = featureFlags.includes(ETL_FEATURE_FLAG);

    const isETLUpgradeAvailable = isETL && !hasEtlFlag && !isLoadingUans;

    let processedUansData = searchDataSources(searchText, rawUansData, showArchivedUans, adminMode);

    if (sortType) {
        processedUansData = processedUansData.sort((uan1, uan2) => {
            return sortByType[sortType](uan1[sortedColName], uan2[sortedColName], isSortAscending);
        });
    }

    processedUansData = processedUansData.map(row => translateMessage(row, translate));

    if (uanIdToShowFirst || lastUpdatedUanId) {
        const targetUanId = uanIdToShowFirst || lastUpdatedUanId;
        processedUansData.sort(({ uanId: uanId1 }, { uanId: uanId2 }) => {
            return sortBool(uanId1 === targetUanId, uanId2 === targetUanId);
        });
    }

    let shelfHeader = selectedUan && selectedUan.display_name ? `${getAdnetworkDisplayName(selectedUan.name, selectedUan.display_name)} Configuration` : '';

    if (adminMode && selectedUan && selectedUan.uan_id) {
        const headerParts = [`uan_id: ${selectedUan.uan_id}`];
        if (selectedUan.dataConnectorId) {
            headerParts.push(`data_connector_id: ${selectedUan.dataConnectorId}`);
        }
        shelfHeader = shelfHeader.concat(` (${headerParts.join(', ')})`);
    }

    const pageHeaderSubText = {
        [PAGE_DATA_TYPES.DATA_SOURCES]: {
            faqLink: 'https://support.singular.net/hc/en-us/articles/360031000051-Data-Connectors-FAQ',
            tourId: TOUR_LINK_IDS.dataConnectors,
        },
        [PAGE_DATA_TYPES.DATA_DESTINATIONS]: {
            faqLink: `https://support.singular.net/hc/en-us/articles/360037917571`,
            tourId: TOUR_LINK_IDS.dataDestinations,
        },
    };

    const ExternalShelfElement = externalShelfElement;
    const isEditMode = !!selectedUan?.uan_id;

    const handleCloseShelf = () => {
        adnetworkBasicLoginHasChanges ? setShowCancelDialog(true) : onCloseShelf();
    };

    const actionComponents = getActionComponentsConfigs();

    const pageHeaderText = redirectPath.includes('extract-data-destinations')
        ? 'STATIC.PAGE_HEADERS.EXTRACT_DATA_DESTINATIONS'
        : `STATIC.PAGE_HEADERS.${translationPageKey}`;

    // TODO: reduce minWidth and fix headers accordingly. atm less than 1400 causes a bug with sticky thead
    return (
        <PageWrapper
            style={{ overflowX: 'initial', minWidth: !isLoadingUans && rawUansData.length > 0 ? 1080 : 1000 }}
            onMouseEnter={() => {
                hoverStartTime = new Date();
            }}
            onMouseLeave={() => {
                const hoverTime = new Date() - hoverStartTime;
                if (hoverTime > 8000) {
                    trackMixpanelEvent(mixpanelPrefix, 'Hover on Page', { hoverTime });
                }
            }}
        >
            <WizardWarningMessage
                containerStyle={{
                    position: 'absolute',
                    width: '100%',
                    top: 0,
                    left: 0,
                    paddingLeft: '40px',
                    paddingRight: '40px',
                }}
                show={uanAction && uanAction.uan && uanAction.type === 'delete'}
                message={UAN_ACTION_MSG_BY_TYPE[uanActionType]}
                textReplace={{
                    uan_name: uanFromUanAction.display_name,
                }}
                showIcon
                showTypeIcon
                textAlignCenter
                type="info"
                delay={300}
            />
            <PageHeader text={pageHeaderText} subText={pageHeaderSubText[translationPageKey]} />
            {isLoadingUans && <Spinner show expanded />}
            {!isLoadingUans && rawUansData.length === 0 && adNetworkOptions.length > 0 && (
                <AdNetworksEmptyState
                    adNetworkOptions={adNetworkOptions}
                    emptyStateAdNetworks={emptyStateDataConnectors}
                    translationPageKey={translationPageKey}
                    mixpanelPrefix={mixpanelPrefix}
                    onAdNetworkSelectionChange={onAdNetworkSelectionChange}
                />
            )}
            {!isLoadingUans && rawUansData.length > 0 && (
                <div>
                    <div className={css.header} ref={pageRef}>
                        <div className={css.dataConnectorsTopSection}>
                            <div className={css.dataConnectorsSection}>
                                <div className={css.search}>
                                    <TextField
                                        clearable
                                        value={searchText}
                                        debounce={500}
                                        onChange={onSearchChange}
                                        containerClass={css.searchInput}
                                        placeholder={translate(`STATIC.PAGES.${translationPageKey}.TYPE_NETWORK_NAME`)}
                                    />
                                </div>
                                <div className={css.addNewUanSection}>
                                    {showFieldsAvailabilityFeature && !isETL && (
                                        <div
                                            className={css.fieldsAvailability}
                                            onClick={() => toggleFieldsAvailabilityShelf({}, true)}
                                        >
                                            <GlassesIcon className={css.fieldsAvailabilityIcon} />
                                            <div className={css.fieldsAvailabilityLabel}>
                                                <Translate id="STATIC.PAGES.DATA_SOURCES.FIELDS_AVAILABILITY_BUTTON" />
                                            </div>
                                        </div>
                                    )}
                                    {adminMode && !isETL && (
                                        <div className={css.yfDataStatus} onClick={openYfDataStatusShelf}>
                                            <DataAvailabilityIcon className={css.fieldsAvailabilityIcon} />
                                            <div className={css.fieldsAvailabilityLabel}>
                                                <Translate id="STATIC.PAGES.DATA_SOURCES.YF_DATA_STATUS.BUTTON" />
                                            </div>
                                        </div>
                                    )}
                                    {getNewUanComponent()}
                                </div>
                            </div>
                            <div className={css.dataConnectorsSlackSection}>
                                {archivedToggleEnabled && (
                                    <div>
                                        <div className={css.slackContainer} onClick={onSlackAction}>
                                            <span className={css.line} />
                                            <Tooltip
                                                style={{ marginRight: 'auto' }}
                                                title={
                                                    isUserSlackAuth
                                                        ? 'Click here to stop receiving the daily Slack alerts'
                                                        : 'Get daily alerts on Slack on the status of your Data Connectors'
                                                }
                                            >
                                                <span className={css.slackText}>
                                                    <SlackIcon className={css.slackImg} />
                                                    {isUserSlackAuth ? 'Remove alerts from Slack' : 'Alert me on Slack'}
                                                </span>
                                            </Tooltip>
                                        </div>
                                        <Toggle
                                            style={{ float: 'right', width: '362px' }}
                                            checked={showArchivedUans}
                                            label={translate('STATIC.PAGES.DATA_SOURCES.TOGGLE_ARCHIVED_UANS')}
                                            onToggle={onToggleArchivedUans}
                                            styleOverride={{
                                                labelStyle: {
                                                    color: '#334155',
                                                    fontSize: '13px',
                                                },
                                            }}
                                        />
                                    </div>
                                )}
                                {searchText && processedUansData.length === 0 && (
                                    <div className={css.noDataContainer}>
                                        <EmptyState
                                            icon="noDataSource"
                                            headerTextReplace={{ searchText: `"${searchText}"` }}
                                            header="STATIC.PAGES.DATA_SOURCES.NO_SEARCH_RESULTS"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <Table
                        highlightRowIndex={
                            uanIdToShowFirst || (uanAction.type && uanAction.type !== 'delete')
                                ? processedUansData.findIndex(uanData => uanData.uanId === uanIdToShowFirst)
                                : -1
                        }
                        theadClass={css.thead}
                        tbodyClass={css.tbody}
                        thStyle={{
                            padding: '10px 10px 10px 25px',
                            fontWeight: 600,
                            textAlign: 'center',
                            boxShadow: '1px 1px 3px #F4F7FA',
                        }}
                        tableClass={css.table}
                        data={processedUansData}
                        metadata={parseMetadata(
                            getMetadataConfig(actionComponents, isETL),
                            timezone,
                            sortedColName,
                            isSortAscending,
                            searchText,
                            {
                                lastDataImportDate: translate(
                                    `STATIC.PAGES.${translationPageKey}.TABLE.TOOLTIPS.LAST_DATA_IMPORT_DATE`
                                ),
                                etlStatus: translate(`STATIC.PAGES.${translationPageKey}.TABLE.TOOLTIPS.ETL_STATUS`),
                            }
                        )}
                        zebraTable
                        hasActionComponentCell
                    />
                </div>
            )}
            <GeneralPopup
                open={showDeleteDialog}
                type={PopupTypes.DELETE}
                title={`STATIC.PAGES.${translationPageKey}.DELETE_MODAL.TITLE`}
                text={`STATIC.PAGES.${translationPageKey}.DELETE_MODAL.MESSAGE`}
                acceptText={`STATIC.PAGES.${translationPageKey}.DELETE_MODAL.ACCEPT_BUTTON_TEXT`}
                rejectText={`STATIC.PAGES.${translationPageKey}.DELETE_MODAL.REJECT_BUTTON_TEXT`}
                onAccept={onAcceptUanDelete}
                onReject={onPopupClose}
                showSpinner={uanDeleteInProcess}
            >
                <div className={css.deleteBody}>
                    <ul className={css.deleteEffects}>
                        <li>{translate(`STATIC.PAGES.${translationPageKey}.DELETE_MODAL.DELETE_EFFECT_QUERIES`)}</li>
                        {uanToDeleteWarnings.length > 0 ? (
                            <li>
                                {translate(
                                    `STATIC.PAGES.${translationPageKey}.DELETE_MODAL.DELETE_EFFECT_CUSTOM_CONFIG`
                                )}
                            </li>
                        ) : null}
                    </ul>
                    {uanToDeleteWarnings.length > 0 ? (
                        <p className={css.deleteTip}>
                            {'\u{1F4A1} '}
                            {translate(`STATIC.PAGES.${translationPageKey}.DELETE_MODAL.CUSTOM_CONFIG_TIP`, {
                                supportLink:
                                    '<a target="_blank" href="mailto:support@singular.net">support@singular.net</a>',
                            })}
                        </p>
                    ) : null}
                </div>
            </GeneralPopup>
            <GeneralPopup
                open={showErrorPopup}
                type={PopupTypes.INFO}
                headerStyle={{ fontSize: '18px', fontWeight: '600' }}
                title={`STATIC.PAGES.${translationPageKey}.ERROR_MODAL.TITLE`}
                text={`STATIC.PAGES.${translationPageKey}.ERROR_MODAL.MESSAGE`}
                acceptText={`STATIC.PAGES.${translationPageKey}.ERROR_MODAL.ACCEPT_BUTTON_TEXT`}
                onReject={onPopupClose}
            />
            {selectedUan && (
                <GeneralPopup
                    open={showCancelDialog}
                    type={PopupTypes.INFO_WITH_CANCEL}
                    title={`STATIC.PAGES.${translationPageKey}.CANCEL_MODAL.TITLE`}
                    text={
                        NETWORKS_WITH_ACCOUNTS_NAMES.includes(selectedUan.name)
                            ? `STATIC.PAGES.${translationPageKey}.CANCEL_MODAL.MESSAGE_WITH_ACCOUNTS`
                            : `STATIC.PAGES.${translationPageKey}.CANCEL_MODAL.MESSAGE`
                    }
                    acceptText={`STATIC.PAGES.${translationPageKey}.CANCEL_MODAL.ACCEPT_BUTTON_TEXT`}
                    rejectText={`STATIC.PAGES.${translationPageKey}.CANCEL_MODAL.REJECT_BUTTON_TEXT`}
                    onAccept={onAcceptCancelAdnetworkLoginShelf}
                    onReject={onPopupClose}
                />
            )}
            {selectedUan && (
                <>
                    {/* In the future we will want to split DataDestinationsPage from DataSourcesPage component */}
                    {ExternalShelfElement && shelf.open ? (
                        <ExternalShelfElement
                            {...shelf}
                            adminMode={adminMode}
                            onClose={handleCloseShelf}
                            {...(isEditMode ? { uan: selectedUan } : { adnetwork: selectedUan })}
                            networkData={{ ...selectedUan, ...selectedRowData }}
                            translate={translate}
                        />
                    ) : (
                        <Shelf
                            {...shelf}
                            shelfSize={selectedUan.name === 'facebook' ? 'xlarge' : 'medium'}
                            headerText={shelfHeader}
                            headerImagePath={selectedUan.logo_path}
                            imageStatusColor={selectedRowData && STATUSES_COLOR_MAP[selectedRowData.status]}
                            closeOnClickOutside={false}
                            onClose={handleCloseShelf}
                        >
                            <AdnetworkBasicLogin
                                networkData={{ ...selectedUan, ...selectedRowData }}
                                adnetworks={adnetworks}
                                adminMode={adminMode}
                                partnersIds={partnersIds}
                                onCancel={handleCloseShelf}
                                translate={translate}
                                onAuth={uanId => {
                                    route(redirectPath, { id: uanId });
                                }}
                                onChange={onAdnetworkLoginChange}
                            />
                        </Shelf>
                    )}
                </>
            )}
            <Shelf
                open={openFieldsAvailability}
                shelfSize="medium"
                headerText="STATIC.PAGES.FIELDS_AVAILABILITY_SHELF.HEADER_TEXT"
                HeaderImage={GlassesIcon}
                headerImageClassName={css.fieldsAvailabilityShelfIcon}
                closeOnClickOutside
                onClose={closeFieldsAvailabilityShelf}
            >
                <FieldsAvailability
                    adnetworkName={selectedUanAdnetworkName}
                    onClose={closeFieldsAvailabilityShelf}
                    reportType={reportTypes.mainReport}
                />
            </Shelf>
            {openYfDataStatus && (
                <Shelf
                    open
                    shelfSize="xlarge"
                    headerText="STATIC.PAGES.DATA_SOURCES.YF_DATA_STATUS.SHELF_HEADER"
                    headerIconName="calendar-check"
                    closeOnClickOutside
                    onClose={() => setOpenYfDataStatus(false)}
                >
                    <YfDataStatus translate={translate} adminMode={adminMode} />
                </Shelf>
            )}
            {dataShelfUan && (
                <Shelf
                    open
                    shelfSize="large"
                    headerText={dataShelfUan.displayName}
                    headerImagePath={dataShelfUan.imgSrc}
                    imageStatusColor={STATUSES_COLOR_MAP[dataShelfUan.status]}
                    closeOnClickOutside
                    onClose={() => toggleDataRefreshShelf({}, false, false)}
                >
                    <DataShelf
                        uanId={dataShelfUan.uanId}
                        uanName={dataShelfUan.displayName}
                        uanStatus={dataShelfUan.status?.toString()}
                        onClose={() => toggleDataRefreshShelf({}, false, false)}
                        translate={translate}
                        dataRefreshOpen={openDataRefresh}
                        canDataRefresh={dataShelfUan.canDataRefresh}
                        earliestDataDate={dataShelfUan.earliestDataDate}
                        timezone={dataShelfUan.orgTimezone}
                        channel={dataShelfUan.channel}
                    />
                </Shelf>
            )}
            {dataConnectorAlertsUan && (
                <Shelf
                    open={openDataConnectorAlerts}
                    shelfSize="large"
                    headerText={`${dataConnectorAlertsUan.displayName} Data Connector Alerts`}
                    HeaderImage={DataConnectorAlertsIcon}
                    headerImageClassName={css.dataConnectorAlertsShelfIcon}
                    onClose={() => toggleDataConnectorAlertsShelf({}, false)}
                >
                    <DataConnectorAlerts
                        uanId={dataConnectorAlertsUan.uanId}
                        onClose={() => toggleDataConnectorAlertsShelf({}, false)}
                        isUserSlackAuth={isUserSlackAuth}
                        onDeleteUserSlackAuth={onDeleteUserSlackAuth}
                        translate={translate}
                    />
                </Shelf>
            )}
            {schemaColumnsUan && (
                <Shelf
                    open
                    shelfSize="large"
                    headerText={translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.TITLE')}
                    closeOnClickOutside
                    onClose={() => toggleSchemaColumnsShelf(null)}
                >
                    <SchemaColumnsShelf
                        uanId={schemaColumnsUan.uanId}
                        translate={translate}
                    />
                </Shelf>
            )}
            {isETLUpgradeAvailable && <UpgradeETLBanner />}
        </PageWrapper>
    );
};

DataSourcesPage.propTypes = {
    uansData: PropTypes.arrayOf(PropTypes.any),
    adnetworks: PropTypes.objectOf(PropTypes.any),
    partnersIds: PropTypes.objectOf(PropTypes.any),
    adminMode: PropTypes.bool,
    translate: PropTypes.func,
    export2Phase: PropTypes.func,
    openShelf: PropTypes.func,
    onCloseShelf: PropTypes.func,
    onUanDelete: PropTypes.func,
    onDeleteUserSlackAuth: PropTypes.func,
    selectedUanData: PropTypes.objectOf(PropTypes.any),
    shelf: PropTypes.objectOf(PropTypes.any),
    apiRequestFailed: PropTypes.bool,
    export2phaseLatestTaskKey: PropTypes.string,
    username: PropTypes.string,
    mixpanelUserParams: PropTypes.shape({
        tier: PropTypes.string,
        is_trial: PropTypes.bool,
        is_web: PropTypes.bool,
        is_agency: PropTypes.bool,
        is_partner: PropTypes.bool,
        is_internal_test_account: PropTypes.bool,
        org_creation_date: PropTypes.string,
        permissions_role: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    isLoadingUans: PropTypes.bool,
    uanAction: PropTypes.objectOf(PropTypes.any),
    updatedUanId: PropTypes.number,
    timezone: PropTypes.string,
    onValidationFinished: PropTypes.func.isRequired,
    adNetworkOptions: PropTypes.arrayOf(PropTypes.any),
    redirectPath: PropTypes.string,
    translationPageKey: PropTypes.string,
    archivedToggleEnabled: PropTypes.bool,
    emptyStateDataConnectors: PropTypes.arrayOf(PropTypes.string),
    mixpanelPrefix: PropTypes.string,
    template2phaseLink: PropTypes.string,
    isUserSlackAuth: PropTypes.bool,
    isStandardAnalytics: PropTypes.bool,
    showFieldsAvailabilityFeature: PropTypes.bool,
    featureFlags: PropTypes.arrayOf(PropTypes.string),
    etlStatusEnabled: PropTypes.bool,
};

export { DataSourcesPage };
export default withLocalize(DataSourcesPage);
